import React, { useEffect } from "react";
import { useState, useRef } from "react";
import useStateWithCallback from "use-state-with-callback";
import { useNavigate } from "react-router";
import { CommonUtil } from "../../../utils/CommonUtil";
import { Constants } from "../../Languages/Constants";
import InspectionRenderer from "../../InspectionGrid/InspectionRenderer";
import { IAGrid } from "../../../Components/IInterfaces";
import InterchangeDocumentStatusRenderer from "../../InterchangeApplication/InterchangeDocumentStatusRenderer";
import { NetworkManager } from "../../../Components/NetworkManager";
import InspectionGrid from "../../InspectionGrid/InspectionGrid";
import BrokerStatusRenderer from "../../InterchangeApplication/BrokerStatusRenderer";
import Offcanvas from "../../../Components/OffCanvas";
import InspectedImages from "./InspectedImages";
import ToastManager from "../../../Components/ToastManager";
import Loader from "../../../Components/Loader";

const ClosedDocGrid = () => {
  const navigate = useNavigate();
  const saasOrgId = CommonUtil.getSaasOrgId();
  const userGridRef1 = useRef<IAGrid>(null);
  const [popupInfo, setPopupInfo] = useState<any>("");
  const [documentId, setDocumentId] = useState<any>(null);
  const [imagesData, setImagesData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [imageCount, setImageCount] = useState<number>(0);
  const [users, setUsers] = useStateWithCallback([], () => {
    if (userGridRef1.current) {
      userGridRef1.current.columnFittoSize();
    }
  });

  const onLoginError = (err: any) => {
    console.log(err);
  };

  useEffect(() => {
    getDocInspection();
  }, [documentId]);

  const getDocInspection = () => {
    if (!documentId) {
      return;
    }
    setLoading(true);
    const orgUrl = `api/dashboard/compare?orgId=${saasOrgId}&isToCompareDifferentDocs=false&docId=${documentId}`;
    NetworkManager.sendJsonRequest(orgUrl, "", "", "GET", true, onDocInspection, onLoginError);
  };
  
  const onDocInspection = (dataObj: any) => {
    const restData = JSON.parse(dataObj);
    if (restData && !restData.errorMessage) {
      if (restData?.inspectionDetails?.length) {
        documentImages(restData.inspectionDetails);
        setLoading(false);
      }
    } else {
      ToastManager.showToast(restData?.errorMessage || "Unknown error", "Error");
    }
  }

  const documentImages = (data: any[]) => {
    const images = data?.map(document => ({
      documentId: document.documentId,
      docTypeName: document.docTypeName || undefined,
      documentCode: document.documentCode,
      vinNumber: document?.vinNumber,
      brand: document?.brand,
      unitNumber: document?.unitNumber,
      licenseNumber: document?.licenseNumber,
      saasOrgId: document.saasOrgId,
      Images: document.processDetails?.flatMap((process: any) =>
        process.taskDetails?.flatMap((task: any) =>
          task?.images?.map((img: any) => ({img: img?.imagePath, comment: img?.comment})) || []
        )
      )
    }));

    const totalImageCount = images?.reduce((acc: number, doc: any) => {
      return acc + (doc.Images?.length || 0);
    }, 0);

    setImageCount(totalImageCount);
    setImagesData(images);
  };
  
  useEffect(() => {
    if(imageCount > 0){
      setPopupInfo({type: "ImgDownload"});
    }
  }, [imageCount]);


  const [userColumns] = useState([
    {
      field: "documentCode",
      headerName: CommonUtil.getMessageText(
        Constants.DOCUMENT_ID,
        "Document No"
      ),
      resizable: true,
      flex: 1,
      width: 100,
      sortable: true,
      cellStyle: {
        fontWeight: "400",
        letterSpacing: "0px",
        fontSize: "12px",
        fontFamily: "poppins",
        color: "black",
      },
      headerClass: "header-white",
      filter: "agTextColumnFilter",
      cellRenderer: InspectionRenderer,
    },
    {
      field: 'equipmentType',
      headerName: "Equipment Type",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'unitNumber',
      headerName: "Unit Number",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'vinNumber',
      headerName: "Vin Number",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: "creationDate",
      headerName: "Creation Date",
      flex: 1,
      sortable: true, 
      resizable: true, 
      cellStyle: {
        fontWeight: "400",
        letterSpacing: "0px",
        fontSize: "13px",
        fontFamily: "poppins",
      },
      headerClass: "header-white",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) =>
        CommonUtil.getDateInterchange([params.data.creationDate]),
    },
    {
      field: "createdBy",
      headerName: CommonUtil.getMessageText(Constants.CREATED_BY, "Created By"),
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: {
        fontWeight: "400",
        letterSpacing: "0px",
        fontSize: "13px",
        fontFamily: "poppins",
      },
      headerClass: "header-white",
      filter: "agTextColumnFilter",
    },
    {
      field: 'carrierName',
      headerName: "Carrier Name",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'brokerName',
      headerName: "Broker Name",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: "documentStatus",
      headerName: CommonUtil.getMessageText(Constants.STATUS, `Status`),
      resizable: true,
      flex: 1,
      sortable: true,
      headerClass: 'header-white',
      cellStyle: baseStyle,
      filter: 'agTextColumnFilter',
      cellRenderer: BrokerStatusRenderer
    },
    {
      field: "action",
      headerName: "Action",
      width: 50,
      resizable: true,
      flex: 1,
      // sortable: true,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter',
      cellRenderer: (props: any) => {
        return (
          <div className={`${props?.data?.imageCount > 0 ? "" : "d-none"} text-center`} role='button' title={`Download & Share ${props?.data?.imageCount} Images`}>
            <img src="../../images/download-lite.svg" alt="download" style={{width: "18px"}} 
              onClick={() => onActionClick(props)} />
          </div>
        )
      }
    },
  ]);

  
  const onActionClick = (props: any) => {
    setDocumentId(props?.data?.documentId);
  };

  useEffect(() => {
    getAllEntrySubmitted();
  }, []);

  const onClickUserDetails = (userData: any) => {
    localStorage.setItem("documentId", userData.documentId);
    navigate("/menu/inspectionDashboard/inspectionDocument");
  };

  const getAllEntrySubmitted = () => {
    setLoading(true);
    const orgUrl = `api/dashboard/doc-filters?orgId=${saasOrgId}&status=CLOSED`;
    NetworkManager.sendJsonRequest(orgUrl, "", "", "GET", true, onGetAllEntrySubmitted, onLoginError);
  };

  const onGetAllEntrySubmitted = (dataObj: any) => {
    if (dataObj) {
      let restData = JSON.parse(dataObj);
      // if (restData) {
      //   restData.map((org: any) => {
      //     console.log(org.documentId);
      //   });
      // }
      setUsers(restData);
      if (restData) {
        let users = [];
        if (Array.isArray(restData)) {
          users = restData;
        } else {
          users.push(restData);
        }
        let ascendingUsers: any = [...restData].sort((a, b) => {
          return b.documentId - a.documentId;
        });
        setUsers(ascendingUsers);
      }
    }
    setLoading(false);
  };

  return (
    <>
    <div className="container-fluid container-fluid-padding mt-3"
      style={{ backgroundColor: "white" }}
    >
      <div className="row fs-7 pt-1">
      {loading && <Loader />}
        <div className="ag-theme-alpine ag-theme-customeTheme gridHeight"
          style={{height: "calc(100vh - 330px)", width: "100%", paddingLeft: "8px", paddingRight: "5px"}}
        >
          <InspectionGrid cols={userColumns} rows={users} pageSize={100} addNew={true}
            tooltip={"Add Company"} name={"Add Company"} headerHeight={CommonUtil.GRID_HEADER_HEIGHT}
            rowHeight={CommonUtil.GRID_ROW_HEIGHT} ref={userGridRef1} method={{ onClickUserDetails }}
          />
        </div>
      </div>
    </div>
    <Offcanvas 
      show={popupInfo?.type === "ImgDownload"}
      onHide={() => {
        setPopupInfo(null);
        setDocumentId(null);
        setImagesData([]);
        setImageCount(0);
      }}
      title="Download / Share Pictures"
      size="70%"
    >
      <InspectedImages data={imagesData} imageCount={imageCount} show={popupInfo?.type === "ImgDownload"} />
    </Offcanvas>
    </>
  );
};

const baseStyle = {
  fontWeight: '400',
  letterSpacing: '0px',
  fontSize: '13px',
  fontFamily: 'poppins',
  paddingTop: '5px'
};

export default ClosedDocGrid;
